import config from 'customer-flows/config/environment'
import { defineExperiment, type AnyExperimentOf, type ExperimentForFlag } from 'customer-flows/config/experiments-d'

/**
 * Canonical registry of all A/B experiments — the single source of truth
 * read by {@link variantFor}.
 */
export const Experiments = {
  NEXTGEN_CONVERSION: defineExperiment({
    flag: 'nextgen-conversion',
    variants: ['test', 'control'],
    default: 'control'
  }),
  FROM_PRICE_DISPLAY: defineExperiment({
    flag: 'from-price-display',
    variants: ['test', 'control'],
    default: 'control'
  })
} as const

type ExperimentFlag = AnyExperimentOf<typeof Experiments>['flag']

/**
 * Resolves an experiment by flag against `config.experiments` (injected
 * server-side by fire-pit before any JS runs — see fire-pit's
 * `replaceRequestVars`). Never returns null: falls back to the experiment's
 * configured `default`.
 *
 * @example
 * ```ts
 * import experiments from 'customer-flows/config/experiments'
 *
 * experiments.variantFor('nextgen-conversion').is('test') // boolean
 * ```
 */
function variantFor<TFlag extends ExperimentFlag>(flag: TFlag) {
  const experiment = Object.values(Experiments).find((candidate) => candidate.flag === flag) as ExperimentForFlag<
    typeof Experiments,
    TFlag
  >
  const value = config.experiments?.[flag] ?? experiment.default

  return {
    value,
    is: (variant: ExperimentForFlag<typeof Experiments, TFlag>['variants'][number]): boolean => value === variant
  }
}

/**
 * Resolved variant for every registered experiment, keyed by flag —
 * used to auto-decorate outbound tracking events with active experiment
 * context. Never includes nulls: unresolved experiments fall back to
 * their configured `default`, same as {@link variantFor}.
 */
function allVariants(): Record<string, string> {
  return Object.values(Experiments).reduce<Record<string, string>>((variants, experiment) => {
    variants[experiment.flag] = String(variantFor(experiment.flag).value)
    return variants
  }, {})
}

export default { variantFor, allVariants }
